import { useState, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import type { PatientVisitDto } from '@ctms/contracts';
import { z } from 'zod';
import { AlertCircle, CalendarDays, X } from 'lucide-react';

import { getApiErrorMessage } from '../../../../api/client.js';
import { visitsApi } from '../../../visits/visits.api.js';
import { VisitHourPicker } from './visit-hour-picker.js';

/**
 * The booked day is REQUIRED to book (`min(1)`), so an empty field is a readable
 * Polish message instead of a format complaint. The exact `YYYY-MM-DD` shape is
 * held by the native date input and re-validated by the API contract
 * (`z.iso.date()` on the wire, REQ-VIS-015) — the form does not duplicate it.
 *
 * The hour is no longer a free-typed native input: it is picked from the slot list
 * of `VisitHourPicker` (D4b/c), which always yields a valid `HH:MM` or `''`.
 */
const ScheduleVisitFormSchema = z.object({
  date: z.string().trim().min(1, 'Data umówienia jest wymagana.'),
});

type ScheduleVisitFormInput = z.infer<typeof ScheduleVisitFormSchema>;

/**
 * Visit-window presentation for the read-only preview (rework II.3, Piotr's
 * mail 2026-10-10). The window is stored RELATIVE in the protocol catalog
 * (REQ-TRIAL-009: min/opt/max days from a referral visit); the caller anchors
 * it to the referral visit's day from the patient's plan and passes the result
 * here — the dialog only renders it.
 */
export interface VisitWindowView {
  /** Allowed absolute range, e.g. `2026-09-21 → 2026-10-02`; `null` when the referral day is unknown. */
  rangeText: string | null;
  /** Anchor + relative span, e.g. `od V1 · 2026-10-05 (plan) · min -14 / opt -7 / max -3 dni`. */
  detailText: string | null;
}

export interface ScheduleVisitDialogProps {
  /** The Domain 3 visit whose planned day is booked (`patient_visits.id`). */
  visitId: number;
  /** Short label of the visit for the header, e.g. `V12` (presentation only). */
  visitLabel: string;
  /** Day already booked (`YYYY-MM-DD`) or `null` when the visit is not scheduled yet. */
  currentDate: string | null;
  /** Hour already booked (`HH:MM`) or `null` when not scheduled yet. */
  currentTime: string | null;
  /**
   * Rework II.3: a REGISTERED visit (`date_real` set) opens the dialog as a
   * read-only preview — visit window + booked data, every field locked, no
   * save/clear. The API-side 409 (`enrollments.service.ts`, „stakeholder note
   * II.3") stays as the second gate; this mode never calls it.
   */
  readOnly?: boolean;
  /** Performed day (`date_real`, `YYYY-MM-DD`) — shown in the preview mode. */
  dateReal?: string | null;
  /** Visit window computed by the caller from the protocol catalog (preview mode). */
  windowInfo?: VisitWindowView | null;
  /** Called with the visit as the SERVER stored it — the caller re-renders from that. */
  onSaved: (visit: PatientVisitDto) => void;
  onClose: () => void;
}

const fieldClass =
  'mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-xs text-slate-100';
const labelClass = 'text-xs text-slate-400';

/**
 * REQ-VIS-015: books the planned DAY and optional HOUR of one visit. Opened from the visit table of
 * the patient chart (element 9 of the Cockpit legend) — the day plan cannot host
 * the action, because a plan row only exists once the date is set.
 *
 * The dialog is deliberately minimal (date + optional time + save) per the session-6/8
 * decision: the target UX of planning and confirmation is still open
 * (`Q-VIS-020`), so this atom does not close it — it only makes the write
 * possible, which is what unblocks the day plan, `REJESTRUJ` and the procedure
 * panel on the test environment.
 *
 * Clearing the booking is an EXPLICIT, confirmed action, never a side effect of
 * an empty field: an omitted date would be a 400 on the wire, and a `null` is a
 * deliberate undo — the two must not look the same to the user.
 */
export function ScheduleVisitDialog({
  visitId,
  visitLabel,
  currentDate,
  currentTime,
  readOnly = false,
  dateReal = null,
  windowInfo = null,
  onSaved,
  onClose,
}: ScheduleVisitDialogProps): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isClearing, setIsClearing] = useState(false);
  // D4b/c: the picked hour lives in the DIALOG's state (session-only), populated from the
  // server value so a correction does not start from blank.
  const [time, setTime] = useState<string>(currentTime ?? '');
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ScheduleVisitFormInput>({
    resolver: zodResolver(ScheduleVisitFormSchema),
    defaultValues: {
      date: currentDate ?? '',
    },
  });

  const saveMutation = useMutation({
    mutationFn: ({ date, time }: { date: string | null; time: string | null }) =>
      visitsApi.scheduleVisit(visitId, date, time),
    onSuccess: (visit) => {
      onSaved(visit);
    },
    onError: async (err: unknown) => {
      setErrorMessage(await getApiErrorMessage(err, 'Nie udało się zapisać daty umówienia.'));
    },
  });

  const isSubmitting = saveMutation.isPending;

  const onSubmit = (input: ScheduleVisitFormInput): void => {
    setErrorMessage(null);
    saveMutation.mutate({ date: input.date, time: time.length > 0 ? time : null });
  };

  const onClear = (): void => {
    setErrorMessage(null);
    saveMutation.mutate({ date: null, time: null });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="schedule-visit-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
    >
      <div className="w-full max-w-md rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-800 pb-4">
          <div>
            <h2
              id="schedule-visit-title"
              className="flex items-center gap-2 text-base font-semibold text-slate-100"
            >
              <CalendarDays className="h-4 w-4 text-cyan-400" />
              {readOnly ? `Podgląd wizyty ${visitLabel}` : `Umów wizytę ${visitLabel}`}
            </h2>
            <p className="mt-1 text-xs text-slate-400">
              {readOnly
                ? 'Wizyta zarejestrowana — dane wyłącznie do podglądu, bez możliwości zmiany.'
                : 'Data umówienia trafia do planu dnia tego dnia i odblokowuje przycisk rejestracji.'}
            </p>
          </div>
          {/* Rework II.3: the preview keeps ONE close control (the footer
              button) — two controls named „Zamknij" in one dialog are an
              a11y ambiguity. */}
          {!readOnly ? (
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              aria-label="Zamknij"
              className="rounded-md p-1.5 text-slate-400 hover:text-slate-200"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>

        <form
          onSubmit={(event) => {
            void handleSubmit(onSubmit)(event);
          }}
          className="mt-5 space-y-4"
          noValidate
        >
          {errorMessage !== null && (
            <div
              role="alert"
              className="flex items-center gap-2 rounded-md border border-rose-800/60 bg-rose-950/40 p-3 text-xs text-rose-300"
            >
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          {readOnly ? (
            <div>
              <p className={labelClass}>Okno wizyty (dopuszczalny zakres dat)</p>
              {windowInfo === null ? (
                <p className="mt-1 text-xs text-slate-300">bez okna</p>
              ) : (
                <>
                  <p className="mt-1 text-sm font-medium text-slate-100">
                    {windowInfo.rangeText ?? '—'}
                  </p>
                  {windowInfo.detailText !== null ? (
                    <p className="mt-0.5 text-xs text-slate-400">{windowInfo.detailText}</p>
                  ) : null}
                </>
              )}
            </div>
          ) : null}

          <div>
            <label htmlFor="schedule-visit-date" className={labelClass}>
              Data umówienia
            </label>
            <input
              id="schedule-visit-date"
              type="date"
              disabled={readOnly || isSubmitting}
              className={`${fieldClass} scheme-dark`}
              {...register('date')}
            />
            {errors.date !== undefined && (
              <p className="mt-1 text-xs text-rose-400">{errors.date.message}</p>
            )}
          </div>

          {readOnly ? (
            <div>
              <p className={labelClass}>Godzina umówienia (opcjonalnie)</p>
              <p className="mt-1 text-xs text-slate-100">{currentTime ?? '—'}</p>
            </div>
          ) : (
            <VisitHourPicker value={time} onChange={setTime} disabled={isSubmitting} />
          )}

          {readOnly ? (
            <div>
              <p className={labelClass}>Data wykonania</p>
              <p className="mt-1 text-xs text-slate-100">{dateReal ?? '—'}</p>
            </div>
          ) : null}

          <div className="flex justify-end gap-2 border-t border-slate-800 pt-4">
            {readOnly ? (
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
              >
                Zamknij
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500 disabled:opacity-50"
                >
                  {isSubmitting ? 'Zapisywanie…' : 'Zapisz'}
                </button>
              </>
            )}
          </div>
        </form>

        {!readOnly && currentDate !== null && (
          <div className="mt-4 border-t border-slate-800 pt-4">
            {isClearing ? (
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-amber-300">
                  Odwołać umówienie z {currentDate}? Data wykonania zostaje bez zmian.
                </p>
                <span className="flex shrink-0 gap-2">
                  <button
                    type="button"
                    onClick={() => setIsClearing(false)}
                    disabled={isSubmitting}
                    className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
                  >
                    Nie
                  </button>
                  <button
                    type="button"
                    onClick={onClear}
                    disabled={isSubmitting}
                    className="rounded-md border border-amber-700 px-3 py-1.5 text-xs text-amber-200 hover:bg-amber-950/40 disabled:opacity-50"
                  >
                    Tak, odwołaj
                  </button>
                </span>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setIsClearing(true)}
                disabled={isSubmitting}
                className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
              >
                Odwołaj umówienie
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}